/* eslint-disable max-len */
import { memoAll } from '../../../CodesForFoundation';
import { clamp } from '../../../CodesThatsGottaGoSomewhereElse';
import { bindCommand, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, component, ComponentTuple, ObjectManager, property } from '../../../concepts/objects';
import { DateType } from '../../../concepts/objects/DateType';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Enum } from '../../../platform/Enum';
import { round } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { clearSelection } from '../../canvas/commands/SelectionCommands';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { DropInfo, DropTarget } from '../../canvas/entities/DropTarget';
import { Figure, Physical, PotentialInteraction, RelativeTo, TransformHints, TransformScope } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Textable } from '../../canvas/entities/Textable';
import { TextAlignment } from '../../canvas/entities/TextConventions';
import { UIField } from '../../canvas/entities/UIField';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { Commentable } from '../annotations/Commentable';
import { CommentCountAttr, CommentOpenAttr } from '../annotations/CommentAssociations';
import { commentsOpen } from '../annotations/CommentCommands';
import { Tag } from '../annotations/Tag';
import { tagsToggleEdit } from '../annotations/TaggingCommands';
import { TagSupport } from '../annotations/TagSupport';
import { Assignable } from '../assignment/Assignable';
import { TaskRecordAttr } from '../assignment/TaskAssocations';
import { taskOpen } from '../assignment/TaskCommands';
import { Audited } from '../auditing/Audited';
import { AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { Concealable, IsActuallyConcealed, IsConcealedHintVisible } from '../conceal/Concealable';
import { flip } from '../conceal/ConcealCommands';
import { ConnectTarget } from '../connector/ConnectTarget';
import { Graphic } from '../graphics/Graphic';
import { Handwriting, Handwritten } from '../handwriting/Handwritten';
import { Pingable } from '../presentation/Pingable';
import { Token } from '../tokens/Token';
import { Votable } from '../voting/Votable';
import { VoteCountAttr } from '../voting/Vote';
import { votesOpen } from '../voting/VotingCommands';
import { StickyContentField } from './StickyContentField';
import { StickyLayout } from './StickyLayout';
import { stickyPropertyLayout } from './StickyPropertyLayout';

const invocation = defineCommand(
    'stickyInvoke',
    null,
    (r, [id, hint]: [string, string?]) => {
        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        if (hint === 'comments') {
            dispatch(commentsOpen, [id]);
        }
        else if (hint === 'votes') {
            dispatch(votesOpen, [id]);
        }
        else if (hint === 'flip' && IsConcealedHintVisible.get(objects.get(id, Sticky))) {
            dispatch(flip, [[id]]);
        }
        else if (hint === 'task') {
            dispatch(taskOpen, [id, true]);
        }
        else {
            const tag = objects.getMaybe(hint, Tag);
            if (!!tag) dispatch(tagsToggleEdit, [tag.id, true]);
        }
    },
    [],
);

const mutation = defineCommand(
    'stickyMutate',
    null,
    (r, [id]: [string]) => {
        const objects = r.call(ObjectManager);
        const subject = objects.get(id, Sticky);
        if (IsActuallyConcealed.get(subject)) {
            r.call(EffectDirector).trigger(Effects.shake, subject);
        }
        else {
            r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
        }
    },
    [],
);

export const stickyInsert = defineCommand(
    'stickyInsert',
    null,
    (cc, [targetId, itemId]: [string, string]) => {

        // const spatial = cc.call(SpatialIndex);
        const objects = cc.call(ObjectManager);

        const sticky = objects.getMaybe(targetId, Sticky);
        if (!sticky) return;

        const token = objects.getMaybe(itemId, Token);
        const graphic = objects.getMaybe(itemId, Graphic);

        if (!!token) {
            cc.dispatch(clearSelection);
            objects.update(u => {
                sticky.content = token.value;
                u.delete(token.removeSelf(), 'cascade');
            });
        }
        else if (!!graphic) {
            cc.dispatch(clearSelection);
            objects.update(u => {
                sticky.content = `(${graphic.url})`;
                u.delete(graphic.removeSelf(), 'cascade');
            });
        }
    }
);

const stickyDef = {
    name: 'Sticky',
    implements: [Physical,
        Assignable,
        Audited,
        AutoBringToFront,
        AutoLayoutSubject,
        Commentable,
        Concealable,
        ConnectTarget,
        DropTarget,
        GraffitiTarget,
        Handwritten,
        Pingable,
        TagSupport,
        Votable,
        Textable,
    ],
    mutation,
    invocation,
    propertyLayout: stickyPropertyLayout,
};

const stickySize = 150;

@component(stickyDef)
export class Sticky extends RectangularPhysical implements Assignable, AutoBringToFront, Audited, Concealable, ConnectTarget, DropTarget, TagSupport, Textable {

    public static readonly Size = stickySize;

    @property({ type: 'number', default: stickySize })
    public width!: number;

    @property({ type: 'number', default: stickySize })
    public height!: number;

    @property({ type: 'string' })
    public autoColor!: string;

    @property({ type: 'string', default: '#ffc107', style: true })
    public background!: string;

    @property({ type: 'number', default: .75 })
    public blend!: number;

    @property({ type: 'string', default: '', oldName: 'content|value' })
    public content!: string;

    @property({ type: 'number', default: Enum.default(TextAlignment), oldName: 'content|textAlignH', style: true })
    public contentTextAlignH!: TextAlignment;

    @property({ type: 'number', default: Enum.default(TextAlignment), oldName: 'content|textAlignV', style: true })
    public contentTextAlignV!: TextAlignment;

    @property({ type: 'boolean' })
    public concealed!: boolean;

    @property({ type: 'string' })
    public taskId!: string;

    @property({ type: 'string' })
    public userId!: string;

    @property({ type: DateType })
    public created!: Date;

    public get boundary() {
        return false;
    }

    @memoUsing('userId', IsActuallyConcealed)
    public get contentFontFamily() {

        if (IsActuallyConcealed.get(this)) {
            return 'redacted_scriptregular';
        }
        else {
            return Handwriting.resolve(this);
        }
    }

    @memoUsing(
        'children', 'width', 'height', 'content', 'contentFontFamily', 'contentTextAlignH', 'contentTextAlignV',
        CommentCountAttr, CommentOpenAttr, TaskRecordAttr, VoteCountAttr,
    )
    public get layout() {
        return StickyLayout(this);
    }

    @memoUsing('layout')
    public get uiFields(): readonly UIField[] {
        return [
            StickyContentField(this)
        ];
    }

    public get shouldSnap() {
        return true;
    }

    // public queryTransformActions(singular: boolean): TransformAction[] {
    //     const bounds = this.bounds(RelativeTo.Model);
    //     return [{
    //         key: 'left',
    //         position: bounds.pointAt(0, .5),
    //         orientation: 'left',
    //         command: bindCommand(alert, ['Test']),
    //         visibility: 'always',
    //         style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //     }, {
    //         key: 'top',
    //         position: bounds.pointAt(.5, 0),
    //         orientation: 'up',
    //         command: bindCommand(alert, ['Test']),
    //         visibility: 'always',
    //         style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //     }, {
    //         key: 'right',
    //         position: bounds.pointAt(1, .5),
    //         orientation: 'right',
    //         command: bindCommand(alert, ['Test']),
    //         visibility: 'always',
    //         style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //     }, {
    //         key: 'bottom',
    //         position: bounds.pointAt(.5, 1),
    //         orientation: 'down',
    //         command: bindCommand(alert, ['Test']),
    //         visibility: 'always',
    //         style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //     }];
    // }

    public queryDrop(mpt: Point, subjects: Iterable<Component>): DropInfo {

        if (this.content !== '') return null;

        const arr = Array.from(subjects);
        if (arr.length !== 1) return null;

        const [item] = arr;
        if (!item.is(Token) && !item.is(Graphic)) return null;

        const toModel = this.transform(RelativeTo.Model);
        const rpt = this.toRelative(mpt);

        return {
            target: this,
            guides: [{ points: toModel.apply(this.layout.base) }],
            command: bindCommand(stickyInsert, [this.id, item.id]),
        };
    }

    @memoAll()
    public queryTransformHints(singular: boolean) {
        return TransformHints({
            scaleX: !!singular,
            scaleY: false,
            rotate: false,
            translate: true,
            visible: !singular,
        });
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const tlt = this.modelLocalTransform();
        from = tlt.apply(from);
        to = tlt.apply(to);

        const [a, b, c] = scope.type === 'scale' ? this.jiggleForPotentialMirror(from, to) : to;

        // prev
        const px = from[0].x;
        const pw = Point.vector(from[0], from[1]).length();

        // width & height vectors
        const wv = Point.vector(a, b);
        const hv = Point.vector(b, c);

        // Calcualte new center point
        const nc = Rect.fromPoints(to).center();

        // Making use of the TransformScope information, if we can tell that the transform is a scale only upon a single Sticky, we should
        // prevent the sticky from moving so the resize action doesn't feel too strange.  To do this, restrict to cases where x movement
        // is within a single resize range and the width actually changes.
        if (scope.type === 'scale' && scope.items.length === 1) {

            const xd = Math.abs(px - a.x);

            const x = px + clamp(a.x - px, 100);
            const w = Math.min(Math.max(pw + clamp(wv.length() - pw, 100), stickySize), stickySize + 100);

            if (this.width !== w && xd >= 0 && xd <= 100) {
                this.x = round(x, 4);
                this.y = round(nc.y - (stickySize / 2), 4);
                this.r = round(0, 4);
                this.width = round(w, 4);
                this.height = round(stickySize, 4);
            }
        }
        else {

            const oc = Rect.fromPoints(from).center();
            // const np = nc.subtract([(pw / 2), (stickySize / 2)]);// .add(Point.vector(oc, nc));
            const np = nc.add([pw / -2, stickySize / -2]);// .add(Point.vector(oc, nc).multiply(.5));

            this.x = round(np.x, 4);
            this.y = round(np.y, 4);
            this.r = round(0, 4);
            this.width = round(pw, 4);
            this.height = round(stickySize, 4);
        }

        return this;
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {

        const { base } = this.layout;
        const headline = base.set({ top: -18, height: 36 });

        const all = Rect.fromMany([
            base,
            headline,
        ]);

        return Rect.fromPoints(this.transform(to).apply(all));
    }

    public test(mpt: Point): boolean {
        const lpt = this.toRelative(mpt);
        const { base } = this.layout;
        if (base.contains(lpt)) return true;
        if (base.pointAt(.5, 0).distanceTo(lpt) <= 18) return true;
        return false;
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const { base, tags, task } = this.layout;
        const lpt = this.toRelative(mpt);
        // Test invoke on any of the annotations
        if (type === 'invoke') {
            for (const [id, area] of Object.entries(tags)) {
                if (id === 'all') continue;
                if (area.contains(lpt)) return [true, id];
            }
            if (task?.frame.contains(lpt)) {
                return [true, 'task'];
            }
            // if (layout.comments.contains(lpt)) return [true, 'comments'];
            // if (layout.votes.contains(lpt)) return [true, 'votes'];
            if (base.pointAt(.5, 0).distanceTo(lpt) <= 18) return [true, 'flip'];
        }
        // Any non invokes on the whole frame
        if (base.contains(lpt)) return [type !== 'invoke', 'default'];
        return [false, 'none'];
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].content = '';
        ct[2].taskId = '';
        return ct;
    }

    public toShallowTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        // If supposed to be concealed, erase content from tuple
        if (IsActuallyConcealed.get(this)) {
            ct[2].content = '';
        }
        return ct;
    }

    public toHumanString(): string {
        return `Sticky ("${this.content.slice(0, 20)}")`;
    }

    public toText(): string {
        return this.content;
    }
}
